import { useEffect, useMemo, useState } from 'react';
import { Box, Chip, CircularProgress, Typography, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import PostalMime from 'postal-mime';
import DOMPurify from 'dompurify';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import { tokens } from '../../../global/theme/tokens';
import { formatBytes } from '../explorer/MediaInfo';
import DateTimeLabel from '../../label/DateTimeLabel';

function formatAddress(entry) {
  if (!entry) return '';
  if (entry.name && entry.address) return `${entry.name} <${entry.address}>`;
  return entry.address || entry.name || '';
}

function formatAddressList(list) {
  return (list || []).map(formatAddress).filter(Boolean).join(', ');
}

function bufferToDataUri(buffer, mimeType) {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  const chunkSize = 0x8000;
  for (let i = 0; i < bytes.length; i += chunkSize) {
    binary += String.fromCharCode(...bytes.subarray(i, i + chunkSize));
  }
  return `data:${mimeType || 'application/octet-stream'};base64,${btoa(binary)}`;
}

// Replaces cid: references in the HTML body with data URIs of the matching
// inline attachments, so embedded images render inside the sandboxed iframe
// (which has an opaque origin and therefore cannot load blob: URLs).
function resolveInlineImages(html, attachments) {
  let resolved = html;
  (attachments || []).forEach((attachment) => {
    if (!attachment.contentId || !attachment.content) return;
    const cid = attachment.contentId.replace(/^<|>$/g, '');
    if (!resolved.includes(`cid:${cid}`)) return;
    const dataUri = bufferToDataUri(attachment.content, attachment.mimeType);
    resolved = resolved.split(`cid:${cid}`).join(dataUri);
  });
  return resolved;
}

const HeaderRow = ({ label, value }) => {
  if (!value) return null;
  return (
    <Box className="flex flex-row gap-2 min-w-0">
      <Typography
        variant="caption"
        sx={{ opacity: 0.6, minWidth: '40px', flexShrink: 0 }}
      >
        {label}
      </Typography>
      <Typography variant="caption" className="truncate">
        {value}
      </Typography>
    </Box>
  );
};

/**
 * Preview for .eml files: fetches the raw RFC 822 source, parses it in the
 * browser (postal-mime) and renders the headers, the sanitized HTML or text
 * body and the attachment list.
 */
const EmlPreview = ({ src }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);
  const { t } = useTranslation();

  const [email, setEmail] = useState(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setEmail(null);
    setFailed(false);
    if (!src) return undefined;
    fetch(src)
      .then((response) => {
        if (!response.ok) throw new Error(`fetch failed (${response.status})`);
        return response.arrayBuffer();
      })
      .then((buffer) => PostalMime.parse(buffer))
      .then((parsed) => {
        if (!cancelled) setEmail(parsed);
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, [src]);

  const bodyDoc = useMemo(() => {
    if (!email?.html) return null;
    const withImages = resolveInlineImages(email.html, email.attachments);
    // same hardening as the email client's conversation view
    const clean = DOMPurify.sanitize(withImages, {
      USE_PROFILES: { html: true },
      ADD_ATTR: ['target'],
      FORBID_TAGS: ['script'],
      FORBID_ATTR: ['onerror', 'onclick', 'onload'],
    });
    return `<!doctype html>
      <html>
        <head>
          <meta charset="utf-8" />
          <base target="_blank" />
          <style>
            body {
              margin: 12px;
              font-family: Arial, Helvetica, sans-serif;
              font-size: 14px;
              color: #222;
              background: #fff;
              word-break: break-word;
            }
            img { max-width: 100%; height: auto; }
          </style>
        </head>
        <body>${clean}</body>
      </html>`;
  }, [email]);

  const regularAttachments = (email?.attachments || []).filter(
    (attachment) => attachment.disposition !== 'inline',
  );

  function downloadAttachment(attachment) {
    const url = URL.createObjectURL(
      new Blob([attachment.content], {
        type: attachment.mimeType || 'application/octet-stream',
      }),
    );
    const link = document.createElement('a');
    link.href = url;
    link.download = attachment.filename || 'attachment';
    link.click();
    URL.revokeObjectURL(url);
  }

  if (failed)
    return (
      <Box className="w-full h-full flex items-center justify-center opacity-60">
        <Typography variant="body2">{t('No Preview')}</Typography>
      </Box>
    );

  if (!email)
    return (
      <Box className="w-full h-full flex items-center justify-center">
        <CircularProgress size="1.5rem" />
      </Box>
    );

  return (
    <Box className="w-full h-full flex flex-col overflow-hidden">
      {/* Header */}
      <Box
        className="flex flex-col gap-0.5 px-3 py-2"
        sx={{ borderBottom: `1px solid ${colors.glass}` }}
      >
        <Box className="flex flex-row items-baseline justify-between gap-2">
          <Typography
            variant="body2"
            className="truncate"
            sx={{ fontWeight: 600 }}
          >
            {email.subject || '-'}
          </Typography>
          {email.date && (
            <Typography
              variant="caption"
              sx={{ opacity: 0.6, flexShrink: 0 }}
            >
              <DateTimeLabel dateTime={email.date} />
            </Typography>
          )}
        </Box>
        <HeaderRow label={t('From')} value={formatAddress(email.from)} />
        <HeaderRow label={t('To')} value={formatAddressList(email.to)} />
        <HeaderRow label="CC" value={formatAddressList(email.cc)} />
      </Box>

      {/* Body */}
      <Box className="flex-1 min-h-0 overflow-hidden">
        {bodyDoc ? (
          <iframe
            title="Email"
            sandbox="allow-popups allow-popups-to-escape-sandbox"
            srcDoc={bodyDoc}
            className="w-full h-full"
            style={{ border: 'none', background: '#fff' }}
          />
        ) : (
          <Box className="w-full h-full overflow-y-auto px-3 py-2">
            <Typography
              variant="body2"
              component="pre"
              sx={{ whiteSpace: 'pre-wrap', fontFamily: 'inherit' }}
            >
              {email.text || ''}
            </Typography>
          </Box>
        )}
      </Box>

      {/* Attachments */}
      {regularAttachments.length > 0 && (
        <Box
          className="flex flex-row flex-wrap gap-1 px-3 py-2"
          sx={{ borderTop: `1px solid ${colors.glass}` }}
        >
          {regularAttachments.map((attachment, index) => (
            <Chip
              key={`${attachment.filename}-${index}`}
              size="small"
              icon={<AttachFileIcon sx={{ fontSize: '14px' }} />}
              label={`${attachment.filename || t('Attachments')}${
                attachment.content?.byteLength
                  ? ` (${formatBytes(attachment.content.byteLength)})`
                  : ''
              }`}
              onClick={() => downloadAttachment(attachment)}
            />
          ))}
        </Box>
      )}
    </Box>
  );
};

export default EmlPreview;
